import React, { useMemo, useState, useEffect } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  CardMedia,
  Typography,
  Box,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import logoSvg from "../imgs/logo.png";
import BottomNav from "./BottomNav";
import { GOOGLE_MAPS_API_KEY } from "../firebase";
import Map from "../components/Map";
import useUserData from "../hooks/useUserData";
import useOtherUsersData from "../hooks/useOtherUsersData";

const libraries = ["places"];

const Home = () => {
  const location = useLocation();
  const [userLocation, setUserLocation] = useState(null);
  const [center, setCenter] = useState({ lat: 40.7128, lng: -74.006 });
  const [locationError, setLocationError] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [activeUserId, setActiveUserId] = useState(null);
  const { userData, profileImageUrl } = useUserData();
  const otherUsersData = useOtherUsersData(userData);

  const loaderOptions = useMemo(
    () => ({
      googleMapsApiKey: GOOGLE_MAPS_API_KEY,
      libraries,
    }),
    []
  );

  const { isLoaded, loadError } = useJsApiLoader(loaderOptions);

  const requestLocationPermission = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setCenter({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationError(
            "No se pudo obtener tu ubicación. Por favor, verifica los permisos de tu navegador."
          );
        }
      );
    } else {
      setLocationError(
        "La geolocalización no está soportada por este navegador."
      );
    }
  };

  useEffect(() => {
    requestLocationPermission();
  }, []);

  useEffect(() => {
    if (location.state?.refresh) {
      requestLocationPermission();
    }
  }, [location.state?.refresh]);

  const handleMarkerClick = (userId) => {
    setActiveUserId(userId);
    setShowInfoWindow(true);
  };

  const handleInfoWindowClose = () => {
    setShowInfoWindow(false);
    setActiveUserId(null);
  };

  if (loadError) return <div>Error loading maps: {loadError.message}</div>;
  if (!isLoaded) return <div>Loading maps</div>;

  return (
    <Container sx={{ padding: "0px" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: "100%",
          height: "68px",
          background: "rgba(8,11,20,0.88)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
        }}
      >
        <Toolbar sx={{ height: "68px", px: 3, display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "68px !important" }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.45rem", lineHeight: 1.1, letterSpacing: "-0.5px", background: "linear-gradient(135deg, #00d4ff, #7b2ff7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              FindMe
            </Typography>
            <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.3)", letterSpacing: "2px", textTransform: "uppercase", mt: 0.2 }}>
              Mapa en vivo
            </Typography>
          </Box>
          <Box sx={{ width: 42, height: 42, borderRadius: "13px", background: "linear-gradient(135deg, rgba(0,212,255,0.18), rgba(123,47,247,0.18))", border: "1px solid rgba(0,212,255,0.28)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 20px rgba(0,212,255,0.18)", overflow: "hidden" }}>
            <CardMedia component="img" image={logoSvg} title="FindMe" sx={{ width: 28, height: 28, objectFit: "contain" }} />
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ padding: "0px" }}>
        {locationError && (
          <Typography color="error">{locationError}</Typography>
        )}
        <Map
          center={center}
          userLocation={userLocation}
          otherUsersData={otherUsersData}
          showInfoWindow={showInfoWindow}
          activeUserId={activeUserId}
          handleMarkerClick={handleMarkerClick}
          handleInfoWindowClose={handleInfoWindowClose}
          userData={userData}
          profileImageUrl={profileImageUrl}
        />
      </Container>
      <BottomNav />
    </Container>
  );
};

export default Home;
